import { motion } from "framer-motion";
import { X, ArrowCounterClockwise, ArrowSquareIn } from "@phosphor-icons/react";
import { FloatingPanel } from "../primitives/FloatingPanel";
import { EquationRenderer } from "./EquationRenderer";
import { TransformPalette } from "./TransformPalette";
import { ParameterSlider } from "./ParameterSlider";
import { StepAnimator } from "./StepAnimator";
import { useEquationPlaygroundStore, type TransformType } from "../../stores/equationPlayground";
import { useEditorStore } from "../../stores/editor";
import { cn } from "../../lib/cn";

const spring = { type: "spring" as const, stiffness: 400, damping: 28 };

export function EquationPlayground() {
  const {
    isOpen,
    equation,
    originalEquation,
    steps,
    parameters,
    isTransforming,
    error,
    close,
    applyTransform,
    updateParameter,
    undoStep,
    jumpToStep,
    getInsertEquation,
    setEquation,
  } = useEquationPlaygroundStore();

  const editorView = useEditorStore((s) => s.editorView);

  const handleInsert = () => {
    const latex = getInsertEquation();
    if (!editorView || !latex) return;

    const { from, to } = editorView.state.selection.main;
    editorView.dispatch({
      changes: { from, to, insert: latex },
    });
    close();
  };

  const handleTransform = (transform: TransformType) => {
    applyTransform(transform);
  };

  const currentStepIndex = steps.length - 1;

  return (
    <FloatingPanel
      open={isOpen}
      onClose={close}
      anchor="center"
      usePortal
      backdrop
      className="w-[480px] max-h-[80vh] overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[var(--border-hairline)] px-4 py-3">
        <h3 className="text-[13px] font-semibold text-[var(--text-primary)]">
          Equation Playground
        </h3>
        <div className="flex items-center gap-1">
          {steps.length > 0 && (
            <motion.button
              onClick={undoStep}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={spring}
              className="flex h-6 w-6 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)] transition-colors"
              title="Undo last step"
            >
              <ArrowCounterClockwise size={13} weight="bold" />
            </motion.button>
          )}
          <motion.button
            onClick={close}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={spring}
            className="flex h-6 w-6 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)] transition-colors"
          >
            <X size={13} weight="bold" />
          </motion.button>
        </div>
      </div>

      <div className="flex flex-col gap-3 p-4 overflow-y-auto max-h-[calc(80vh-100px)]">
        {/* Equation display */}
        <EquationRenderer latex={equation} size="lg" />

        {/* LaTeX source input */}
        <div className="relative">
          <input
            type="text"
            value={equation}
            onChange={(e) => setEquation(e.target.value)}
            placeholder="Enter LaTeX expression..."
            className={cn(
              "w-full rounded-[var(--radius-md)] px-3 py-2",
              "bg-[var(--bg-base)] border border-[var(--border-hairline)]",
              "text-[12px] font-mono text-[var(--text-primary)]",
              "placeholder:text-[var(--text-muted)]",
              "focus:border-[var(--color-lever-orange)]/30 focus:shadow-[var(--glow-ai)]",
              "transition-all duration-[var(--duration-fast)]",
            )}
          />
        </div>

        {/* Error message */}
        {error && (
          <div className="rounded-[var(--radius-sm)] bg-[var(--color-soft-red)]/8 border border-[var(--color-soft-red)]/15 px-3 py-2 text-[11px] text-[var(--color-soft-red)]">
            {error}
          </div>
        )}

        {/* Transform palette */}
        <div>
          <span className="text-[10px] font-medium uppercase tracking-wider text-[var(--text-muted)] mb-1.5 block">
            Transform
          </span>
          <TransformPalette
            onTransform={handleTransform}
            isTransforming={isTransforming}
          />
        </div>

        {/* Loading indicator */}
        {isTransforming && (
          <div className="flex items-center gap-2 py-1">
            <span className="h-3 w-3 animate-spin rounded-full border-2 border-[var(--color-lever-orange)] border-t-transparent" />
            <span className="text-[11px] text-[var(--text-muted)]">Transforming...</span>
          </div>
        )}

        {/* Parameter sliders */}
        {parameters.length > 0 && (
          <div>
            <span className="text-[10px] font-medium uppercase tracking-wider text-[var(--text-muted)] mb-1.5 block">
              Parameters
            </span>
            <div className="flex flex-col gap-2">
              {parameters.map((p) => (
                <ParameterSlider
                  key={p.name}
                  parameter={p}
                  onChange={(val) => updateParameter(p.name, val)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Step history */}
        <StepAnimator
          steps={steps}
          originalEquation={originalEquation}
          currentIndex={currentStepIndex}
          onJumpTo={jumpToStep}
        />

        {/* Insert button */}
        <motion.button
          onClick={handleInsert}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          transition={spring}
          className={cn(
            "flex items-center justify-center gap-2 rounded-[var(--radius-md)] px-4 py-2",
            "bg-[var(--color-lever-orange)] text-white text-[12px] font-medium",
            "hover:brightness-110 hover:shadow-[var(--shadow-md)]",
            "transition-all duration-[var(--duration-fast)]",
          )}
        >
          <ArrowSquareIn size={14} weight="bold" />
          Insert into document
        </motion.button>
      </div>
    </FloatingPanel>
  );
}

EquationPlayground.displayName = "EquationPlayground";
